// Crossword geometry: the pure, dependency-free part the generator and the
// player share. A grid is an array of N strings, one per row; '#' is a black
// square, anything else is a white cell (a letter, or '.' for "unfilled").
//
// Both sides must agree on what a "slot" is and how numbering works - the
// generator numbers the clues it stores, and the player renders those numbers
// over the same cells - so this file is imported by both rather than being
// reimplemented twice. No Vue, no Firebase, no Node APIs.

export const BLACK = '#';
export const EMPTY = '.';

export const isBlack = (grid, r, c) => grid[r][c] === BLACK;

/**
 * Every maximal horizontal/vertical run of white cells. A run of length 1 or
 * 2 is still returned (callers validating a pattern need to see them); the
 * minimum-length rule is theirs to apply.
 *
 * Each slot: { dir: 'across'|'down', row, col, len, cells: [[r,c], ...] }.
 * Across first in row-major order, then down in row-major order of their
 * START cell - which is the order numbering wants.
 */
export function findSlots(grid) {
  const n = grid.length;
  const slots = [];
  for (let r = 0; r < n; r += 1) {
    let c = 0;
    while (c < n) {
      if (isBlack(grid, r, c)) { c += 1; continue; }
      const start = c;
      while (c < n && !isBlack(grid, r, c)) c += 1;
      const cells = [];
      for (let k = start; k < c; k += 1) cells.push([r, k]);
      slots.push({ dir: 'across', row: r, col: start, len: c - start, cells });
    }
  }
  for (let c = 0; c < n; c += 1) {
    let r = 0;
    while (r < n) {
      if (isBlack(grid, r, c)) { r += 1; continue; }
      const start = r;
      while (r < n && !isBlack(grid, r, c)) r += 1;
      const cells = [];
      for (let k = start; k < r; k += 1) cells.push([k, c]);
      slots.push({ dir: 'down', row: start, col: c, len: r - start, cells });
    }
  }
  return slots;
}

/**
 * Standard crossword numbering: scan row-major; a white cell gets the next
 * number if it starts an across word (left edge or black to the left, with a
 * white to the right) or a down word (top edge or black above, with a white
 * below). Returns { numbers: {"r,c": n}, across: [slot...], down: [slot...] }
 * with each slot carrying its `num`. Slots shorter than minLen are ignored
 * (an unchecked 1- or 2-cell run is not a word).
 */
export function numberGrid(grid, minLen = 3) {
  const n = grid.length;
  const numbers = {};
  const across = [];
  const down = [];
  const slots = findSlots(grid).filter((s) => s.len >= minLen);
  const byStart = new Map();
  for (const s of slots) {
    const key = `${s.row},${s.col}`;
    if (!byStart.has(key)) byStart.set(key, []);
    byStart.get(key).push(s);
  }
  let next = 1;
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      const here = byStart.get(`${r},${c}`);
      if (!here) continue;
      numbers[`${r},${c}`] = next;
      for (const s of here) {
        const numbered = { ...s, num: next };
        (s.dir === 'across' ? across : down).push(numbered);
      }
      next += 1;
    }
  }
  return { numbers, across, down };
}

/** The word currently in a slot, read off the grid. */
export const wordAt = (grid, slot) => slot.cells.map(([r, c]) => grid[r][c]).join('');

/** 180-degree rotational symmetry, the standard American convention. */
export function isSymmetric(grid) {
  const n = grid.length;
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (isBlack(grid, r, c) !== isBlack(grid, n - 1 - r, n - 1 - c)) return false;
    }
  }
  return true;
}

/** Every white cell reachable from every other (no walled-off corners). */
export function isConnected(grid) {
  const n = grid.length;
  let start = null;
  let whites = 0;
  for (let r = 0; r < n; r += 1) {
    for (let c = 0; c < n; c += 1) {
      if (!isBlack(grid, r, c)) { whites += 1; if (!start) start = [r, c]; }
    }
  }
  if (!start) return false;
  const seen = new Set([`${start[0]},${start[1]}`]);
  const stack = [start];
  while (stack.length) {
    const [r, c] = stack.pop();
    for (const [dr, dc] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nr = r + dr;
      const nc = c + dc;
      if (nr < 0 || nc < 0 || nr >= n || nc >= n) continue;
      if (isBlack(grid, nr, nc)) continue;
      const key = `${nr},${nc}`;
      if (seen.has(key)) continue;
      seen.add(key);
      stack.push([nr, nc]);
    }
  }
  return seen.size === whites;
}

export const countBlack = (grid) => grid.join('').split('').filter((ch) => ch === BLACK).length;

export const toRows = (cells, n) => {
  const rows = [];
  for (let r = 0; r < n; r += 1) rows.push(cells.slice(r * n, (r + 1) * n).join(''));
  return rows;
};
